import { useState } from 'react'

import type { ShipmentStatusEnum, TShipmentMember } from '../../../../../types/shipment'
import type { UserDetails } from '../../../../../types/user'
import type { IWorkspace } from '../../../../../types/workspace'
import { EmailPhoneForm } from '../../shared/email-phone-form'
import { useDriverMutations } from './hooks/use-driver-mutations'
import { useDriversList } from './hooks/use-drivers-list'
import { useMemberSearch } from './hooks/use-member-search'
import { MemberSelectionList } from './member-selection-list'
import { WorkspaceListModal } from './workspace-list-modal'

interface IDriversList {
  drivers: TShipmentMember[]
  onClose: () => void
  shipmentID: string
  shipmentNumber: string
  shipmentStatus: ShipmentStatusEnum
  workspaceID: string
}

/**
 * This component is used to display the list of drivers in the shipment.
 */
export const DriversList = ({
  drivers,
  onClose,
  shipmentID,
  shipmentNumber,
  shipmentStatus,
  workspaceID
}: IDriversList) => {
  const driversList = drivers.map((driver) => {
    return {
      ...driver.user,
      workspaceID: driver.workspace?.id
    }
  })

  const driversWorkspace = drivers
    .map(({ workspace }) => workspace)
    .filter((workspace): workspace is IWorkspace => !!workspace)

  const [openInvitePopup, setOpenInvitePopup] = useState(false)

  const {
    handleAddDriver,
    handleRemoveDriver,
    isLoading: mutationLoading
  } = useDriverMutations(onClose, shipmentID, shipmentNumber, shipmentStatus, workspaceID, () => {
    setOpenInvitePopup(false)
  })

  const { handleSubmit, loading, driverWorkspaceList, handleAssignDriver, resetInviteState, setUserInput, userInput } =
    useDriversList(handleAddDriver, workspaceID)

  const searchResults = useMemberSearch({
    selectedUsers: driversList.map((user) => ({ user })),
    userInput
  })

  const removeDriver = (clickedUser: UserDetails) => {
    const { id } = clickedUser
    handleRemoveDriver({ driverWorkspaceID: clickedUser.workspaceID ?? null, userID: id })
  }

  const assignDriver = (selectedWorkspaceID: string) => {
    handleAssignDriver(selectedWorkspaceID)
  }

  if (openInvitePopup) {
    return (
      <>
        <EmailPhoneForm onSubmit={handleSubmit} loading={mutationLoading || loading} initialValue={userInput} />
        {driverWorkspaceList.length > 0 && (
          <WorkspaceListModal
            workspaceList={driverWorkspaceList}
            onSelectWorkspace={assignDriver}
            onClose={() => resetInviteState()}
            description="This driver belongs to multiple workspaces. Please select one"
          />
        )}
      </>
    )
  }

  return (
    <MemberSelectionList
      members={searchResults}
      onSearchInput={setUserInput}
      selectedMembers={driversList}
      isLoadingMember={mutationLoading}
      onInviteClick={() => setOpenInvitePopup(!openInvitePopup)}
      onMemberSelect={removeDriver}
      onMemberRemove={removeDriver}
      membersWorkspace={driversWorkspace}
    />
  )
}
